import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { COLLECTIONS, CATALOG, NOOP } from "constants/action-types";
import {
    Item,
    LINESHEET_PAGE_SIZE,
    IGridItem,
    ITEMS_ID,
    ONE_SIZE,
    MANUFACTURED_SIZES_DELIMITER,
    ValueTypes,
    CollectionKeys,
} from "constants/product-collection";
import siteConfig from "site-config";
import {
    addHashtags,
    setScrollTo,
    collectionHistoryUndo,
    mutateCollectionItem,
    IMutateCollectionItemPayload,
} from "actions/collections";
import {
    listInsertOperation,
    listMoveOperation,
    listRemoveOperation,
    listSetOperation,
    clear as clearFromStorage,
    mapSetOperation,
} from "actions/mutate";
import regexes from "components/draft-js-mention/utils/hashtagRegex";
import { formatMessage } from "lib/globalize-util";
import _ from "lodash";
import * as Sentry from "@sentry/browser"; // error tacking
import { clearAllSelectedItems } from "actions/bulk-items";
import { add as snackbarAdd, clear as clearSnackbar } from "actions/snackbars";
import { fromJS, List, Map } from "immutable";
import diff from "immutablediff";
import moment from "moment";
import uuid from "uuid/v4";
import { updateEntities } from "redux-query";
import { normalizeData } from "helpers/data/normalize";
import CatalogEntries from "schemas/catalogs/catalog-entries";
import { push } from "react-router-redux";
import {
    syncPatch,
    getFormatBlankFields,
    populateDefaultItem,
    filterOutProtectedKeys,
    syncDateFields,
    getFieldNameToDisplayPropertyMap,
} from "./helpers";
import { applySizeLevelFieldsChanges, shouldSyncSelectedSizes, syncSelectedSizes } from "./helpers/sizing";
import { DEFAULT_ITEM_OBJECT_ID_PATH, getCollectionDefaultItem } from "reducers/selectors/collection";
import { amphibiousGetIn, amphibiousSetIn } from "helpers/objUtils";
import { AssortmentShoppingActions } from "constants/analytics";
import me from "models/me";
import { WindowAnalytics } from "helpers/window-analytics";
import { ISchemaKey, internalGetter, sizeManufacturedGetter } from "helpers/product-collection";
import { delimitedFieldHasSingleValue } from "helpers/sizing";
import { INCLUDED, NOT_INCLUDED } from "containers/product-collections/product-collection-shop-brand-catalog";
import { ActionsObservable } from "redux-observable";
import { IStore } from "reducers";
import { GridApi } from "ag-grid-community";
import { makeAutoSelectSingleResultEpicActions } from "./helpers/collection";

enum ImageFields {
    _cdn_images = "_cdn_images",
    _images = "_images",
    image_url = "image_url",
    image_urls = "image_urls",
}

const BULK_MAX = 2000;

function getHashtags(attributes) {
    const hashtags = [];
    Object.keys(attributes.fields).forEach((key) => {
        const value = attributes.fields[key];
        Object.keys(value).forEach((subkey) => {
            const subvalue = value[subkey];
            let match;
            // eslint-disable-next-line no-cond-assign
            while ((match = regexes.validHashtag.exec(subvalue))) {
                const hashtag = match[3] as string;
                hashtags.push(hashtag);
            }
        });
    });

    return hashtags;
}

const getNextPage = (url: string) => {
    if (url) {
        return ajax(siteConfig.api + url).map(({ response, xhr }) => {
            const next = xhr.getResponseHeader("Next");
            return {
                next,
                response: response.data || response,
            };
        });
    } else {
        return Observable.of({ next: null, response: [] });
    }
};

const fetchProductCollectionItem = ({
    attributes,
    index,
    productId,
    merchantId,
    blankFields,
    productName,
    itemDisplayProperty,
}) => {
    return ajax({
        method: "GET",
        url: `${siteConfig.goAPI}/v1/collection_products/${productId}?with_snapshot=true&seeker_merchant_id=${merchantId}`,
    })
        .map(({ response }) => {
            const { product } = response;
            const attributesId = attributes.fields.id.string_value;
            const fields = product.snapshot.root.mutable_map.fields;
            const productImageKeys = Object.keys(attributes.fields).reduce((accumulator, field) => {
                if (
                    field.includes(ImageFields._cdn_images) ||
                    field.includes(ImageFields._images) ||
                    field.includes(ImageFields.image_url) ||
                    field.includes(ImageFields.image_urls)
                ) {
                    accumulator[field] = { string_value: attributes.fields[field].string_value };
                }
                return accumulator;
            }, {});

            const idFields = {
                fields: {
                    id: { string_value: attributesId },
                    _product_revision: { string_value: product.root_ref.revision },
                    ...productImageKeys,
                },
            };

            const propertyMap = getFieldNameToDisplayPropertyMap(itemDisplayProperty);
            const attributesWithIds = Object.keys(fields).reduce((accumulator: any, key) => {
                const field = fields[key];
                if (accumulator.fields[key] === undefined) {
                    if (field.bool_value !== undefined) {
                        accumulator.fields[key] = {
                            bool_value: field.bool_value,
                        };
                    } else if (
                        typeof field.struct_value === "object" &&
                        propertyMap.getIn([key, "value_type"]) === ValueTypes.DATE
                    ) {
                        accumulator.fields[key] = {
                            date_value: field.struct_value,
                        };
                    } else if (typeof field.struct_value === "object") {
                        accumulator.fields[key] = {
                            struct_value: field.struct_value,
                        };
                    } else {
                        accumulator.fields[key] = {
                            string_value: field.string_value,
                        };
                    }
                }

                return accumulator;
            }, idFields);

            return { attributesWithIds, index };
        })
        .catch((err: any) => {
            try {
                Sentry.captureException(err);
            } catch (e) {
                // ignore
            }

            const productImageKeys = Object.keys(attributes.fields).reduce((accumulator, field) => {
                if (
                    field.includes(ImageFields._cdn_images) ||
                    field.includes(ImageFields._images) ||
                    field.includes(ImageFields.image_url) ||
                    field.includes(ImageFields.image_urls)
                ) {
                    accumulator[field] = { string_value: attributes.fields[field].string_value };
                }
                return accumulator;
            }, {});

            const { blankItem, defaultValueFields } = blankFields;

            const attributesWithIds = {
                fields: {
                    ...blankItem,
                    ...productImageKeys,
                    _product_id: { string_value: productId },
                    id: attributes.fields.id,
                },
            };

            defaultValueFields.forEach((field) => {
                if (!attributesWithIds.fields[field]) {
                    attributesWithIds.fields[field] = { string_value: productName };
                }
            });

            return Observable.of({ attributesWithIds, index, productName });
        });
};

const bulkFetchProducts = (
    results,
    lastIndex,
    merchantId,
    blankFields,
    allowDuplicateProduct,
    itemsId,
    itemDisplayProperty,
    filterInAssortment?: string
) => {
    let index = lastIndex;
    return (action, store) => {
        const { entities, mutate } = store.getState();
        const listProductsAdded =
            !allowDuplicateProduct || filterInAssortment != null ? mutate.snapshots.get(itemsId).toJS() : [];
        const { items, entries } = entities;
        const queries = [];
        results.forEach((id) => {
            const entry = entries[id];
            if (entry.type === "item") {
                const item = items[entry.item_id];
                const productImages = item.__images.reduce((accumulator, image, imageIndex) => {
                    accumulator[`product_image_${imageIndex + 1}`] = image;
                    return accumulator;
                }, {});

                if (!allowDuplicateProduct || filterInAssortment != null) {
                    const isDuplicateProduct = listProductsAdded.some(
                        (productAdded) => productAdded.fields?._product_id?.string_value === entry.item_id
                    );

                    if (isDuplicateProduct) {
                        if (!allowDuplicateProduct || filterInAssortment === NOT_INCLUDED) {
                            return;
                        }
                    } else if (filterInAssortment === INCLUDED) {
                        return;
                    }
                }

                queries.push(
                    fetchProductCollectionItem({
                        attributes: {
                            fields: {
                                id: { string_value: uuid() },
                                _product_id: { string_value: entry.item_id },
                                ...productImages,
                            },
                        },
                        index,
                        productId: entry.item_id,
                        productName: `${item.name} - ${item.color}`,
                        blankFields,
                        merchantId,
                        itemDisplayProperty,
                    })
                );
                index++;
            }
        });
        return { queries, index };
    };
};

// TODO: Refactor to avoid fetch existing snapshot products (NU-15236)
export const createBulkItemsInCollectionEpic = (action$, store) => {
    let index;
    let bulkAddProductsMissing;
    return action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.BULK.START)
        .map(({ payload }) => payload)
        .mergeMap(({ catalogId, itemsId, lastIndex, collectionId, filterInAssortment }) => {
            index = lastIndex;
            const { scope, entities, entries, mutate } = store.getState();
            const { results, nextPage } = entries.collections[catalogId];
            const currentCollection = mutate.snapshots && mutate.snapshots.get(collectionId);
            const items = mutate.snapshots && mutate.snapshots.get(ITEMS_ID);
            const itemDisplayProperty = currentCollection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS, []);
            const allowDuplicateProduct = currentCollection.getIn(
                CollectionKeys.ITEM_DISPLAY_PROPERTY.ALLOW_DUPLICATE_PRODUCT_ENTRIES,
                true
            );
            const blankFields = getFormatBlankFields(itemDisplayProperty, scope.brand);
            const merchantId = currentCollection.getIn(["merchant_v1_id", "string_value"], "");
            const callsLimit = (BULK_MAX - results.length) / LINESHEET_PAGE_SIZE - 1;
            const shouldCopySelectedSizes = shouldSyncSelectedSizes(itemDisplayProperty);
            const beforeItemsCount = items.size;

            return Observable.create((observer) => {
                getNextPage(nextPage)
                    .expand(({ next }, i) => {
                        return next && i < callsLimit ? getNextPage(next) : Observable.empty();
                    })
                    .reduce(
                        (acc: any, res: any) => {
                            const data = normalizeData(res.response, CatalogEntries, entities.schemas, scope);
                            store.dispatch(
                                updateEntities({
                                    items: (currentProducts) => {
                                        return {
                                            ...currentProducts,
                                            ...data.entities.items,
                                        };
                                    },
                                    entries: (currentEntries) => {
                                        return {
                                            ...currentEntries,
                                            ...data.entities.entries,
                                        };
                                    },
                                })
                            );

                            return {
                                entities: {
                                    items: {
                                        ...acc.entities.items,
                                        ...data.entities.items,
                                    },
                                    entries: {
                                        ...acc.entities.entries,
                                        ...data.entities.entries,
                                    },
                                },
                                result: acc.result.concat(data.result),
                            };
                        },
                        { entities: { items: entities.items, entries: entities.entries }, result: results }
                    )
                    .catch((error) => observer.error(error))
                    .subscribe((catalogEntries) => {
                        store.dispatch({
                            meta: {
                                key: catalogId,
                                replace: true,
                            },
                            payload: {
                                results: catalogEntries.result,
                                nextPage: null,
                            },
                            type: CATALOG.ENTRIES.FETCH.COMPLETED,
                        });
                        observer.next({ catalogEntries, itemsId });
                        observer.complete();
                    });
            }).concatMap(({ catalogEntries, itemsId }) => {
                bulkAddProductsMissing = [];
                const queries = bulkFetchProducts(
                    catalogEntries.result,
                    index,
                    merchantId,
                    blankFields,
                    allowDuplicateProduct,
                    itemsId,
                    itemDisplayProperty,
                    filterInAssortment
                )(action$, store);
                index = queries.index;

                if (!queries.queries.length) {
                    return Observable.merge(
                        Observable.of({
                            type: COLLECTIONS.COLLECTION.ITEMS.BULK.COMPLETED,
                            payload: bulkAddProductsMissing,
                        }),
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("Bulk add complete, |count| items added", { count: 0 }),
                            })
                        )
                    );
                }

                return Observable.forkJoin(queries.queries)
                    .flatMap((bulkResponse) => {
                        const collectionDefaultItem = getCollectionDefaultItem(store.getState(), collectionId);
                        const { mutate } = store.getState();
                        const items = mutate.snapshots && mutate.snapshots.get(ITEMS_ID);
                        const latestItemsCount = items.size;
                        const itemsIndexChange = latestItemsCount - beforeItemsCount;

                        const actions = bulkResponse.map((bulkResponseEntry) => {
                            if ((bulkResponseEntry as any).productName) {
                                bulkAddProductsMissing.push((bulkResponseEntry as any).productName);
                            }
                            if (shouldCopySelectedSizes) {
                                (bulkResponseEntry as any).attributesWithIds = syncSelectedSizes(
                                    (bulkResponseEntry as any).attributesWithIds,
                                    itemDisplayProperty
                                );
                            }
                            if (collectionDefaultItem) {
                                (bulkResponseEntry as any).attributesWithIds = populateDefaultItem(
                                    (bulkResponseEntry as any).attributesWithIds,
                                    collectionDefaultItem.toJS(),
                                    itemDisplayProperty
                                );
                            }
                            const vendorSizes = sizeManufacturedGetter(null, {
                                data: (bulkResponseEntry as any).attributesWithIds,
                            });
                            if (delimitedFieldHasSingleValue(vendorSizes, MANUFACTURED_SIZES_DELIMITER)) {
                                (bulkResponseEntry as any).attributesWithIds = amphibiousSetIn(
                                    (bulkResponseEntry as any).attributesWithIds,
                                    Item.FIELDS.SIZES_SELECTED,
                                    vendorSizes
                                );
                            }
                            return listInsertOperation(
                                itemsId,
                                (bulkResponseEntry as any).attributesWithIds,
                                (bulkResponseEntry as any).index + itemsIndexChange
                            );
                        }) as any;

                        const count = actions.length;

                        actions.push({
                            type: COLLECTIONS.COLLECTION.ITEMS.BULK.COMPLETED,
                            payload: bulkAddProductsMissing,
                        });
                        if (index - lastIndex === BULK_MAX) {
                            actions.push(
                                snackbarAdd({
                                    message: formatMessage("Bulk limit of |bulkMax| reached", { bulkMax: BULK_MAX }),
                                })
                            );
                        } else {
                            actions.push(
                                snackbarAdd({
                                    message: formatMessage("Bulk add complete, |count| items added", { count }),
                                })
                            );
                        }
                        WindowAnalytics.track(AssortmentShoppingActions.AddAllProducts, {
                            numberOfProducts: count,
                            userType: me.account_type,
                        });

                        return actions;
                    })
                    .catch((e) => {
                        let errorMessage = e;
                        if (e.error) {
                            errorMessage = e.error;
                        } else if (e.message) {
                            errorMessage = e.message;
                        }
                        throw Observable.throw(errorMessage);
                    });
            });
        })
        .catch((e) => {
            let errorMessage = e;
            if (e.error) {
                errorMessage = e.error;
            } else if (e.message) {
                errorMessage = e.message;
            }
            const payload =
                errorMessage ||
                formatMessage("There was an error completing bulk action. Please refresh and try again");
            return Observable.merge(
                Observable.of({
                    type: COLLECTIONS.COLLECTION.ITEMS.BULK.COMPLETED,
                    error: true,
                    payload,
                }),
                Observable.of(snackbarAdd({ message: payload }))
            );
        });
};

const prepareActionsAfterToFetchSnapshot = ({
    store,
    product,
    attributes,
    itemsId,
    rowId,
    itemDisplayProperty,
    collectionDefaultItem,
    meta,
    selected,
    collectionId,
    undo,
    skipScroll,
}) => {
    const {
        mutate: { snapshots },
        scope: { brand },
    } = store.getState();
    const snapshot = snapshots.get(itemsId);
    const attributesId = rowId || attributes.fields.id.string_value;
    const fields = product.snapshot.root.mutable_map.fields;
    const productImageKeys = Object.keys(attributes.fields).reduce((accumulator, field) => {
        if (
            field.includes(ImageFields._cdn_images) ||
            field.includes(ImageFields._images) ||
            field.includes(ImageFields.image_url) ||
            field.includes(ImageFields.image_urls)
        ) {
            accumulator[field] = { string_value: attributes.fields[field].string_value };
        }
        return accumulator;
    }, {});
    // TODO: Refactor/Validate if `product.root_ref.revision` is required
    const idFields = {
        fields: {
            // call to product_collection/:id doesnt return these atm
            id: { string_value: attributesId },
            _product_revision: { string_value: product.root_ref.revision },
            ...productImageKeys,
        },
    };

    const propertyMap = getFieldNameToDisplayPropertyMap(itemDisplayProperty);
    let attributesWithIds = Object.keys(fields).reduce((accumulator: any, key) => {
        const field = fields[key];
        if (accumulator.fields[key] === undefined) {
            if (field.bool_value !== undefined) {
                accumulator.fields[key] = {
                    bool_value: field.bool_value,
                };
            } else if (
                typeof field.struct_value === "object" &&
                propertyMap.getIn([key, "value_type"]) === ValueTypes.DATE
            ) {
                accumulator.fields[key] = {
                    date_value: field.struct_value,
                };
            } else if (typeof field.struct_value === "object") {
                accumulator.fields[key] = {
                    struct_value: field.struct_value,
                };
            } else {
                accumulator.fields[key] = {
                    string_value: field.string_value,
                };
            }
        }
        return accumulator;
    }, idFields);

    // Making sure a product has brand id based on active brand
    if (!_.get(attributesWithIds, Item.FIELDS.BRAND_ID) && brand) {
        _.set(attributesWithIds, Item.FIELDS.BRAND_ID, brand._id);
    }

    if (shouldSyncSelectedSizes(itemDisplayProperty)) {
        attributesWithIds = syncSelectedSizes(attributesWithIds, itemDisplayProperty);
    }

    if (collectionDefaultItem) {
        attributesWithIds = populateDefaultItem(attributesWithIds, collectionDefaultItem.toJS(), itemDisplayProperty);
    }

    const vendorSizes = sizeManufacturedGetter(null, { data: attributesWithIds });
    if (delimitedFieldHasSingleValue(vendorSizes, MANUFACTURED_SIZES_DELIMITER)) {
        attributesWithIds = amphibiousSetIn(attributesWithIds, Item.FIELDS.SIZES_SELECTED, vendorSizes);
    }

    if (rowId) {
        let foundNode;
        const foundIndex = snapshot.findIndex((node) => {
            foundNode = node;
            return node.getIn(Item.FIELDS.ID) === rowId;
        });

        if (foundIndex === -1) {
            return [snackbarAdd({ message: formatMessage("Item not found") })];
        } else {
            meta.history = false;
            return [
                listSetOperation(itemsId, foundIndex, foundNode.mergeDeep(fromJS(attributesWithIds)), undefined, meta),
                snackbarAdd({ message: formatMessage("Product Linked!") }),
            ];
        }
    }

    const actions: any = [
        addHashtags(getHashtags(attributes)),
        listInsertOperation(itemsId, attributesWithIds, snapshot.size, meta),
        snackbarAdd({
            message: selected
                ? formatMessage("Product added again to assortment")
                : formatMessage("Product added to assortment"),
            action: collectionId && formatMessage("Undo"),
            onActionClick: collectionId && undo,
        }),
    ];
    WindowAnalytics.track(AssortmentShoppingActions.AddProduct, {
        location: window.location.search.includes("mode=cart") ? "Assortment panel" : "Shop",
        userType: me.account_type,
    });

    if (!skipScroll) {
        actions.push(setScrollTo(snapshot.size));
    }

    return actions;
};

export const createItemInCollectionEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEM.CREATE)
        .mergeMap(
            ({
                payload: { itemsId, attributes, index, productId, addType, rowId, skipScroll, collectionId, selected },
                meta,
            }) => {
                const {
                    routing,
                    mutate,
                    scope: { brand },
                    entities,
                } = store.getState();
                const currentCollection = mutate.snapshots && mutate.snapshots.get(collectionId);
                const collectionDefaultItem = getCollectionDefaultItem(store.getState(), collectionId);
                const itemDisplayProperty = currentCollection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS, []);
                const merchantId = currentCollection.getIn(["merchant_v1_id", "string_value"], "");
                const undo = () => {
                    store.dispatch(
                        push({
                            pathname: routing.locationBeforeTransitions.pathname,
                            query: {
                                ...routing.locationBeforeTransitions.query,
                                itemId: undefined,
                                productId: undefined,
                            },
                        })
                    );
                    store.dispatch(collectionHistoryUndo(collectionId));
                    store.dispatch(clearSnackbar());
                };
                if (addType === "virtual" || addType === "clone" || addType === "paste" || addType === "cloneSMU") {
                    if (collectionDefaultItem && addType === "virtual") {
                        attributes = populateDefaultItem(attributes, collectionDefaultItem.toJS(), itemDisplayProperty);
                    }

                    // dont care about doing anything to attributes for these actions besides the new id
                    let actions = [
                        addHashtags(getHashtags(attributes)),
                        listInsertOperation(itemsId, attributes, index),
                    ] as any;

                    if (addType === "cloneSMU") {
                        actions.push(
                            snackbarAdd({
                                message: formatMessage("SMU cloned"),
                                action: collectionId && formatMessage("Undo"),
                                onActionClick: collectionId && undo,
                            })
                        );
                    }

                    if (addType === "virtual" || addType === "clone") {
                        actions.push(
                            snackbarAdd({
                                message:
                                    addType === "virtual"
                                        ? formatMessage("SMU added")
                                        : formatMessage("Product cloned"),
                                action: collectionId && formatMessage("Undo"),
                                onActionClick: collectionId && undo,
                            })
                        );
                    }

                    if (addType === "virtual") {
                        const updatedQuery = {
                            ...routing.locationBeforeTransitions.query,
                            itemId: attributes.fields.id.string_value,
                            productId: undefined,
                        };
                        const openSMUDetailsRoute = push({
                            pathname: routing.locationBeforeTransitions.pathname,
                            query: updatedQuery,
                        });
                        actions = [...actions, openSMUDetailsRoute, setScrollTo(index)];
                    }

                    return actions;
                }
                // Use existing Product that was gotten by collection_fetch_products_by_ids
                if (entities.items && productId) {
                    const product = entities.items[productId]?.product;

                    if (product) {
                        return prepareActionsAfterToFetchSnapshot({
                            store,
                            product,
                            attributes,
                            itemsId,
                            rowId,
                            itemDisplayProperty,
                            collectionDefaultItem,
                            meta,
                            selected,
                            collectionId,
                            undo,
                            skipScroll,
                        });
                    }
                }
                const params = {
                    method: "GET",
                    url: `${siteConfig.goAPI}/v1/collection_products/${productId}?with_snapshot=true&seeker_merchant_id=${merchantId}`,
                };

                return ajax(params)
                    .map(({ response }) => response)
                    .flatMap(({ product }) => {
                        return prepareActionsAfterToFetchSnapshot({
                            store,
                            product,
                            attributes,
                            itemsId,
                            rowId,
                            itemDisplayProperty,
                            collectionDefaultItem,
                            meta,
                            selected,
                            collectionId,
                            undo,
                            skipScroll,
                        });
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.error(error);
                        const item = entities.items[productId];
                        if (item) {
                            const productImageKeys = Object.keys(attributes.fields).reduce((accumulator, field) => {
                                if (
                                    field.includes(ImageFields._cdn_images) ||
                                    field.includes(ImageFields._images) ||
                                    field.includes(ImageFields.image_url) ||
                                    field.includes(ImageFields.image_urls)
                                ) {
                                    accumulator[field] = { string_value: attributes.fields[field].string_value };
                                }
                                return accumulator;
                            }, {});
                            const { blankItem, defaultValueFields } = getFormatBlankFields(itemDisplayProperty, brand);

                            let attributesWithIds: IGridItem = {
                                fields: {
                                    ...blankItem,
                                    ...productImageKeys,
                                    _product_id: { string_value: productId },
                                    id: attributes.fields.id,
                                },
                            };

                            defaultValueFields.forEach((field) => {
                                if (!attributesWithIds.fields[field]) {
                                    attributesWithIds.fields[field] = { string_value: `${item.name} - ${item.color}` };
                                }
                            });

                            if (collectionDefaultItem) {
                                attributesWithIds = populateDefaultItem(
                                    attributesWithIds,
                                    collectionDefaultItem.toJS(),
                                    itemDisplayProperty
                                );
                            }

                            if (
                                amphibiousGetIn(attributesWithIds, Item.FIELDS.VENDOR_SIZES) === ONE_SIZE ||
                                amphibiousGetIn(attributesWithIds, Item.FIELDS.NU_SIZE_CODES) === ONE_SIZE
                            ) {
                                attributesWithIds = amphibiousSetIn(
                                    attributesWithIds,
                                    Item.FIELDS.SIZES_SELECTED,
                                    ONE_SIZE
                                ) as IGridItem;
                            }

                            return Observable.merge(
                                Observable.of(listInsertOperation(itemsId, attributesWithIds, index, meta)),
                                Observable.of(
                                    snackbarAdd({
                                        message:
                                            "This product has not completed its sync and will be added as a placeholder. Sync product data later to fill in the missing fields.",
                                        action: collectionId && formatMessage("Undo"),
                                        onActionClick: collectionId && undo,
                                    })
                                )
                            );
                        } else {
                            return Observable.of(snackbarAdd({ message: formatMessage("Product not found") }));
                        }
                    });
            }
        )
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error);
            return Observable.of(snackbarAdd({ message: error.message }));
        });

export const clearItemsEpic = (action$, store) => {
    return action$.ofType(COLLECTIONS.COLLECTION.CLEAR).mergeMap(({ payload: { itemsId } }) => {
        if (itemsId) {
            return [clearFromStorage(itemsId)];
        }
        return [];
    });
};

export const checkForProductUpdatesEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.START)
        .switchMap(({ payload: { collectionId } }) => {
            const { mutate } = store.getState();

            const collection = mutate.snapshots && mutate.snapshots.get(collectionId);
            const merchantId = collection.getIn(["merchant_v1_id", "string_value"], "");
            const itemsId = collection ? collection.getIn(["items", "ref", "object_id"]) : undefined;
            if (!itemsId) {
                throw new Error("Items not found");
            }

            const items = mutate.snapshots.get(itemsId);

            if (!items) {
                throw new Error("Items not found");
            }

            if (items.size === 0) {
                return Observable.of({
                    payload: {
                        completed: true,
                        collectionId,
                    },
                    type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP,
                });
            }

            return Observable.range(0, items.size) // hack to make this thing keep going
                .concatMap((currentIndex) => {
                    const item = items.get(currentIndex);
                    if (!item) {
                        if (currentIndex >= items.size - 1) {
                            store.dispatch({
                                payload: {
                                    completed: true,
                                    collectionId,
                                },
                                type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP,
                            });
                        }
                        return Observable.empty();
                    }
                    const productId = item.getIn(Item.FIELDS.PRODUCT_ID);
                    const productRevision = item.getIn(Item.FIELDS.PRODUCT_REVISION);
                    if (!productId || !productRevision) {
                        if (currentIndex >= items.size - 1) {
                            store.dispatch({
                                payload: {
                                    completed: true,
                                    collectionId,
                                },
                                type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP,
                            });
                        }
                        return Observable.empty();
                    }
                    return ajax({
                        url: `${siteConfig.goAPI}/v1/collection_products/${productId}?with_snapshot=false&seeker_merchant_id=${merchantId}`,
                        hideBeforeUnloadWarning: true,
                    }).concatMap(({ response = {} }: any) => {
                        if (
                            !response.product ||
                            !response.product.root_ref ||
                            !response.product.root_ref.revision ||
                            response.product.root_ref.revision === productRevision
                        ) {
                            if (currentIndex >= items.size - 1) {
                                store.dispatch({
                                    payload: {
                                        completed: true,
                                        collectionId,
                                    },
                                    type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP,
                                });
                            }
                        } else {
                            store.dispatch({
                                payload: {
                                    isOutOfDate: true,
                                    completed: true,
                                    collectionId,
                                },
                                type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP,
                            });
                        }
                        return Observable.empty();
                    });
                })
                .takeUntil(action$.ofType(COLLECTIONS.COLLECTION.CLEAR))
                .takeUntil(action$.ofType(COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP))
                .catch(() => {
                    return Observable.of({
                        error: true,
                        type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP,
                    });
                });
        });
};

export const saveProductUpdatesEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP)
        .filter((action) => action.payload && action.payload.completed && action.payload.collectionId)
        .mergeMap(({ payload }) => {
            return [mapSetOperation(payload.collectionId, "products_checked_at_ms", moment.utc().valueOf())];
        });
};

export const syncProductsEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.START)
        .switchMap(({ payload: { collectionId, isFiltered } }) => {
            const {
                mutate,
                collection: { gridApi },
            } = store.getState();

            const collection = mutate.snapshots && mutate.snapshots.get(collectionId);
            const merchantId = collection.getIn(["merchant_v1_id", "string_value"], "");
            const itemDisplayProperty = collection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS, []);
            const shouldCopySelectedSizes = shouldSyncSelectedSizes(itemDisplayProperty);
            const itemsId = collection ? collection.getIn(["items", "ref", "object_id"]) : undefined;
            const unfilteredItems = mutate.snapshots.get(itemsId);
            let filteredItems = [];

            // check if isFiltered is boolean and is true
            const hasFilterApplied = typeof isFiltered === "boolean" && isFiltered; // hack for sync filtered products

            if (hasFilterApplied) {
                // Build a set of productIds from filtered nodes
                const filteredProductIds = new Set();
                gridApi.forEachNodeAfterFilter((node) => {
                    filteredProductIds.add(node.data.fields._product_id.string_value);
                });

                // Filter unfilteredItems to only include items that are in the set of filtered productIds
                filteredItems = unfilteredItems.filter((item) => {
                    const productId = item.getIn(Item.FIELDS.PRODUCT_ID);
                    return filteredProductIds.has(productId);
                });
            }

            if (!itemsId) {
                throw new Error("Items not found");
            }

            const items = hasFilterApplied ? filteredItems : unfilteredItems;

            if (!items) {
                throw new Error("Items not found");
            }

            if (items.size === 0) {
                return Observable.of({
                    type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP,
                });
            }

            return Observable.range(0, items.size) // hack to make this thing keep going
                .concatMap((currentIndex) => {
                    const item = items.get(currentIndex);

                    if (!item) {
                        if (currentIndex >= items.size - 1) {
                            store.dispatch({ type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP });
                        }
                        return Observable.empty();
                    }
                    const productId = item.getIn(Item.FIELDS.PRODUCT_ID);
                    const itemId = item.getIn(Item.FIELDS.ID);
                    let productRevision = item.getIn(Item.FIELDS.PRODUCT_REVISION);
                    const originalProductRevision = productRevision;

                    if (!productId || !productRevision) {
                        if (currentIndex >= items.size - 1) {
                            store.dispatch({ type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP });
                        }
                        return Observable.empty();
                    }

                    return ajax({
                        url: `${siteConfig.goAPI}/v1/collection_products/${productId}?with_snapshot=false&seeker_merchant_id=${merchantId}`,
                        hideBeforeUnloadWarning: true,
                    }).concatMap(({ response = {} }: any) => {
                        if (
                            !response.product ||
                            !response.product.root_ref ||
                            !response.product.root_ref.revision ||
                            response.product.root_ref.revision === productRevision
                        ) {
                            if (currentIndex >= items.size - 1) {
                                store.dispatch({ type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP });
                            }
                            return Observable.empty();
                        }
                        if (parseFloat(response.product.root_ref.revision) < parseFloat(productRevision)) {
                            try {
                                Sentry.captureException(
                                    `Collection ${collectionId} product ${productId} revision ${productRevision} ahead of head revision ${response.product.root_ref.revision}`
                                );
                            } catch (e) {
                                // ignore
                            }
                            productRevision = response.product.root_ref.revision;
                        }
                        if (productRevision === "-1") {
                            productRevision = response.product.root_ref.revision;
                        }
                        return ajax({
                            url: `${siteConfig.goAPI}/v1/collection_products/${productId}?with_snapshot=true&seeker_merchant_id=${merchantId}`,
                            hideBeforeUnloadWarning: true,
                        }).concatMap((currentProductAjax) => {
                            return ajax({
                                url: `${siteConfig.goAPI}/v1/collection_products/${productId}?with_snapshot=true&revision=${productRevision}&seeker_merchant_id=${merchantId}`,
                                hideBeforeUnloadWarning: true,
                            }).concatMap((oldProductAjax) => {
                                const currentItems = store.getState().mutate.snapshots.get(itemsId);
                                const index = currentItems.findIndex((node) => node.getIn(Item.FIELDS.ID) === itemId);

                                if (index !== -1) {
                                    let currentItem = currentItems.get(index);

                                    if (currentItem.getIn(Item.FIELDS.PRODUCT_ID) === productId) {
                                        const currentProduct = fromJS(
                                            currentProductAjax.response.product.snapshot.root.mutable_map
                                        );
                                        if (originalProductRevision === "-1") {
                                            currentItem = currentItem.mergeDeep(currentProduct);
                                        } else {
                                            const oldProduct = fromJS(
                                                oldProductAjax.response.product.snapshot.root.mutable_map
                                            );
                                            currentItem = syncPatch({ currentItem, currentProduct, oldProduct });

                                            // Correctly patch the differences between date_values
                                            currentItem = syncDateFields({
                                                currentItem,
                                                currentProduct,
                                                oldProduct,
                                                itemDisplayProperty,
                                            });

                                            // Copy _nu_sizes_manufactured to selected sizes
                                            const isNuSizesManufacturedOutdated =
                                                oldProduct.getIn(Item.FIELDS.NU_SIZES_MANUFACTURED) !==
                                                currentProduct.getIn(Item.FIELDS.NU_SIZES_MANUFACTURED);
                                            if (isNuSizesManufacturedOutdated && shouldCopySelectedSizes) {
                                                currentItem = syncSelectedSizes(currentItem, itemDisplayProperty, true);
                                            }
                                            // Here we remove any manually entered values for size level fields.
                                            currentItem = applySizeLevelFieldsChanges(
                                                currentItem,
                                                currentProduct,
                                                oldProduct,
                                                isNuSizesManufacturedOutdated
                                            );
                                        }
                                        currentItem = currentItem.setIn(
                                            ["fields", "_product_revision", "string_value"],
                                            response.product.root_ref.revision
                                        );
                                        store.dispatch(
                                            listSetOperation(itemsId, index, currentItem, undefined, { syncData: true })
                                        );
                                    }
                                }
                                if (currentIndex >= items.size - 1) {
                                    store.dispatch({ type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP });
                                }
                                return Observable.empty();
                            });
                        });
                    });
                })
                .takeUntil(action$.ofType(COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP))
                .catch(() => {
                    return Observable.of({
                        error: true,
                        type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP,
                    });
                });
        });
};

export const removeItemInCollectionEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEM.REMOVE)
        .map(({ payload }) => payload)
        .flatMap(({ itemsId, index }) => {
            return [listRemoveOperation(itemsId, index)];
        });

export const removeItemsInCollectionEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.REMOVE)
        .map(({ payload }) => payload)
        .flatMap(({ itemsId, bulkItems }) => {
            // get indexees from list
            const {
                collection: { gridApi },
                mutate,
            } = store.getState();

            // Find all indexes
            let indexes = [];
            const snapshot = mutate.snapshots.get(itemsId);
            if (snapshot) {
                bulkItems.forEach((id) => {
                    const index = snapshot.findIndex((item) => item.getIn(Item.FIELDS.ID) === id);
                    if (index !== -1) {
                        indexes.push(index);
                    }
                });
            }

            indexes = _.uniq(indexes);

            // sort bulk items by index desc
            indexes.sort((a, b) => {
                if (a < b) {
                    return 1;
                }
                if (a > b) {
                    return -1;
                }
                if (a === b) {
                    return 0;
                }
            });
            // if this is from the grid, tell it to clear
            if (gridApi) {
                gridApi.deselectAll();
            }
            // listRemoveOperation(itemsId, index),
            const actions = [clearAllSelectedItems()];
            indexes.forEach((index) => {
                actions.push(listRemoveOperation(itemsId, index));
            });
            return actions;
        });

export const moveItemInCollectionEpic = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEM.MOVE)
        .map(({ payload }) => payload)
        .flatMap(({ itemsId, index, destinationIndex, rowId }) => {
            return [listMoveOperation(itemsId, index, destinationIndex, { rowId })];
        });

type Payload = {
    rowId: string;
    rowIndex: number;
    dependentColumnId: string;
};

const makeMutateCollectionItemToEmpty = (ITEMS_ID: string, rowIndex: number, data: Map<any, any>, fieldId: string) => {
    return [
        mutateCollectionItem({
            itemsId: ITEMS_ID,
            index: rowIndex,
            item: data.deleteIn(["fields", fieldId]),
        }),
    ];
};

//TODO: NU-33538 UNIT TESTS
export const autoSelectSingleResultEpic = (
    action$: ActionsObservable<{
        type: string;
        payload: Payload;
    }>,
    store$: { getState: () => IStore }
) =>
    action$.ofType(COLLECTIONS.COLLECTION.ITEMS.ITEM.SYNC_AUTO_SELECT_SINGLE_RESULT).flatMap(({ payload }) => {
        const state = store$.getState();
        const {
            mutate,
            collection: { collectionId, merchantId, gridApi },
        } = state;
        const api = gridApi as GridApi;
        const rowNode = api.getRowNode(payload.rowId);

        const collection = mutate.snapshots.get(collectionId);
        const itemDisplayProperty: List<Map<keyof ISchemaKey, any>> = collection.getIn(
            CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS
        );
        const column = itemDisplayProperty.find(
            (schemaKey) => schemaKey.get("field_name") === payload.dependentColumnId
        );
        const { interalLookup, dependency } = internalGetter(
            column.toJS(),
            merchantId,
            itemDisplayProperty.toJS()
        )(rowNode.data.fields);
        // dependency here is a object, if any value is null we can bailout and not make the call
        const dependenciesValues = Object.values(dependency);

        if (dependenciesValues.some((value) => !value)) {
            // TODO: NU-33538 do not mutate if the values are the same
            return makeMutateCollectionItemToEmpty(
                ITEMS_ID,
                payload.rowIndex,
                fromJS(rowNode.data),
                payload.dependentColumnId
            );
        }
        return Observable.fromPromise(
            interalLookup({ data: dependency, fieldName: payload.dependentColumnId })
        ).flatMap((result: Record<string, any>) => {
            // we need to get store again
            // because the instance in the previous observable is old
            const state = store$.getState();
            const {
                collection: { gridApi },
            } = state;
            const api = gridApi as GridApi;
            const rowNode = api.getRowNode(payload.rowId);
            const immutableData = fromJS(rowNode.data) as Map<any, any>;

            if (result.length === 1) {
                return [
                    mutateCollectionItem({
                        itemsId: ITEMS_ID,
                        index: payload.rowIndex,
                        item: immutableData.setIn(["fields", payload.dependentColumnId, "string_value"], result[0].key),
                    }),
                ];
            }
            if (result.length === 0) {
                return makeMutateCollectionItemToEmpty(
                    ITEMS_ID,
                    payload.rowIndex,
                    immutableData,
                    payload.dependentColumnId
                );
            }
            if (result.length > 1) {
                const currentValue = immutableData.getIn(["fields", payload.dependentColumnId]);
                const possibleResults = result.map(({ key }) => key);
                if (currentValue && !possibleResults.includes(currentValue.get("string_value"))) {
                    return makeMutateCollectionItemToEmpty(
                        ITEMS_ID,
                        payload.rowIndex,
                        immutableData,
                        payload.dependentColumnId
                    );
                }
            }

            // we do nothing when there is more than one possible option and the value
            return [{ type: NOOP }];
        });
    });

export const mutateItemInCollectionEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEM.MUTATE)
        .map(({ payload }) => payload)
        .flatMap(({ itemsId, index, item, value, meta, fieldName }: IMutateCollectionItemPayload) => {
            const state = store.getState();
            const {
                mutate,
                collection: { dependentFieldsLookupMap },
            } = state;

            let mutatedItem;
            if (mutate && mutate.snapshots) {
                // This is in case a set op occurs before receiving the remove op from server
                const currentItems = mutate.snapshots.get(itemsId);
                // This is a source of slowdowns but nessesary evil for now
                mutatedItem = currentItems.find(
                    (currentItem) =>
                        currentItem.getIn(["fields", "id", "string_value"]) ===
                        item.getIn(["fields", "id", "string_value"])
                );

                if (!mutatedItem) {
                    return [];
                }
            }
            const hashtags = [];
            if (value) {
                let match;
                // eslint-disable-next-line no-cond-assign
                while ((match = regexes.validHashtag.exec(value))) {
                    const hashtag = match[3] as string;
                    hashtags.push(hashtag);
                }
            } else {
                item.get("fields").forEach((v) => {
                    v.forEach((subvalue) => {
                        let match;
                        // eslint-disable-next-line no-cond-assign
                        while ((match = regexes.validHashtag.exec(subvalue))) {
                            const hashtag = match[3] as string;
                            hashtags.push(hashtag);
                        }
                    });
                });
            }

            let deltas;
            if (mutatedItem) {
                deltas = diff(mutatedItem, item);
            }
            const actions = [];

            if (hashtags.length) {
                actions.push(addHashtags(hashtags));
            }

            actions.push(listSetOperation(itemsId, index, item, deltas, meta));

            const singleSelectFieldsActions = makeAutoSelectSingleResultEpicActions(
                fieldName,
                dependentFieldsLookupMap,
                {
                    rowIndex: index,
                    rowId: item.getIn(Item.FIELDS.ID),
                }
            );
            return actions.concat(singleSelectFieldsActions);
        });

export const mutateDefaultItem = (action$, store$) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.DEFAULT_ITEM.MUTATE)
        .map(({ payload }) => payload)
        .flatMap(({ collectionId, item }) => {
            const { mutate } = store$.getState();
            const collection = mutate.snapshots && mutate.snapshots.get(collectionId);
            const itemDisplayProperty = collection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS, []);
            const defaultItemObjectId = collection.getIn(DEFAULT_ITEM_OBJECT_ID_PATH);

            const actions = [];

            const mutatedItem = filterOutProtectedKeys(item, itemDisplayProperty);

            actions.push(
                mapSetOperation(
                    defaultItemObjectId,
                    "fields",
                    Map<string, string>([["struct_value", mutatedItem.get("fields")]])
                )
            );
            return actions;
        });
